import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X, Edit, FileText, Loader2 } from "lucide-react";

export default function InvoicePreviewModal({ 
  isOpen, 
  onClose, 
  onEdit, 
  onCreate, 
  previewPdfUrl,
  isLoading 
}) {
  const { t } = useTranslation();
  const [isCreating, setIsCreating] = useState(false);

  // Handle modal close with cleanup
  const handleModalClose = () => {
    onClose();
  };

  // Handle create invoice from preview
  const handleCreate = async () => {
    setIsCreating(true);
    try {
      await onCreate();
      // Modal will be closed by parent component after successful creation
    } catch (error) {
      // Error handling is done in parent component
      console.error('Invoice creation failed:', error);
    } finally {
      setIsCreating(false);
    }
  };

  // Handle edit (go back to form)
  const handleEdit = () => {
    onEdit();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleModalClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 flex flex-col pdf-preview-modal">
        {/* Fixed Header */}
        <DialogHeader className="p-6 pb-4 flex-shrink-0">
          <DialogTitle className="text-xl flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Rechnungsvorschau
          </DialogTitle>
          <p className="text-sm text-gray-600 mt-2">
            Überprüfen Sie Ihre Rechnung vor der finalen Erstellung. Das Layout entspricht exakt der späteren PDF-Datei.
          </p>
        </DialogHeader>
        
        {/* PDF Viewer - Flexible Area */}
        <div className="flex-1 px-6 pb-4 overflow-auto min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-full min-h-[400px] bg-gray-50 rounded-lg">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
                <p className="text-gray-600">Vorschau wird generiert...</p>
              </div>
            </div>
          ) : previewPdfUrl ? (
            <div className="bg-gray-50 rounded-lg p-4 h-full min-h-[400px] pdf-preview-container">
              <object
                data={`${previewPdfUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                type="application/pdf"
                className="w-full h-full border-0 rounded"
                title="Invoice Preview"
              >
                {/* Fallback to iframe if object doesn't work */}
                <iframe
                  src={`${previewPdfUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                  className="w-full h-full border-0 rounded"
                  title="Invoice Preview"
                />
              </object>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full min-h-[400px] bg-gray-50 rounded-lg">
              <div className="text-center">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-500">Vorschau konnte nicht geladen werden</p>
                <p className="text-sm text-gray-400 mt-2">Bitte versuchen Sie es erneut</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Fixed Action Buttons */}
        <div className="flex justify-between items-center p-6 pt-4 border-t bg-gray-50 flex-shrink-0">
          <Button
            variant="outline"
            onClick={handleEdit}
            disabled={isLoading || isCreating}
            className="flex items-center gap-2"
          >
            <Edit className="h-4 w-4" />
            Anpassen
          </Button>
          
          <Button
            onClick={handleCreate}
            disabled={isLoading || isCreating || !previewPdfUrl}
            className="flex items-center gap-2"
          >
            {isCreating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            {isCreating ? 'Wird erstellt...' : 'Erstellen'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
